var extend = require("../../general.js").extend;
var cloneObject = require("../../general.js").clone;
var arrayHash = require("../../hash.js").arrayHash;

var HashMap = require("../../collections/hashMap.js");
var HashSet = require("../../collections/hashSet.js");
var SortedMap = require("../../collections/sortedMap.js");

var ObservableValue = require("./../observableValue.js");
var ObservableTreeValue = require("./observableTreeValue");
var ObservableTreeMap = require("./observableTreeMap.js");
var ObservableTreeSet = require("./observableTreeSet.js");
var ObservableTreeList = require("./observableTreeList.js");

var sanityCheckHandler = require("./../sanityCheckHandler.js");

var Shadows = require("./shadows.js");

// Observable tree node mix in
var ObservableTreeNode = {
    makeObservableTreeNode: function(){
        this.isObservableTreeNode = true;

        this.root = null;   // root that contains this node, if it's added to a root
        this.parent = ObservableValue.createNewFunction(); // parent of this tree node
        this.collections = new HashMap();  // different collections of this tree node which can contain children and references
        this.referrers = new HashSet(arrayHash); // all other nodes that have a reference to this tree node

        this.handlers = {
            bubble: null,
            add: null,
            remove: null,
            init: null,
            die: null
        };

        this.initShadows();
    },

    // Tree node collections:
    // =====================
    value: function(name, owner, validate){
        console.assert(typeof name == "string" && !this.collections.contains(name), 'Invalid collection name: "' + name + '"A collection with this name already exist or name is not a string');
        console.assert(arguments.length<=3, "Too many arguments to create value");

        var value = ObservableTreeValue.createNewFunction(this, name, owner, validate);
        this.collections.put(name, value);
        return value;
    },

    map: function(name, owner, validate){ // TODO: allow key owner?
        console.assert(typeof name == "string" && !this.collections.contains(name), 'Invalid collection name: "' + name + '"A collection with this name already exist or name is not a string');
        console.assert(arguments.length<=3, "Too many arguments to create map");

        var map = new ObservableTreeMap(this, name, owner, validate);
        this.collections.put(name, map);
        return map;
    },

    set: function(name, owner, validate){
        console.assert(typeof name == "string" && !this.collections.contains(name), 'Invalid collection name: "' + name + '"A collection with this name already exist or name is not a string');
        console.assert(arguments.length<=3, "Too many arguments to create set");

        var set = new ObservableTreeSet(this, name, owner, validate);
        this.collections.put(name, set);
        return set;
    },

    list: function(name, owner, validate){
        console.assert(typeof name == "string" && !this.collections.contains(name), 'Invalid collection name: "' + name + '"A collection with this name already exist or name is not a string');
        console.assert(arguments.length<=3, "Too many arguments to create list");

        var list = new ObservableTreeList(this, name, owner, validate);
        this.collections.put(name, list);
        return list;
    },

    // Tree node methods:
    // =================
    // Add this node to a given collection
    addTo: function(collection, key){
        switch (collection.getType()){
            case 'value': collection(this); break;
            case 'map' : collection.put(key, this); break;
            case 'set' : collection.add(this); break;
            case 'list' :
                if (key != undefined) collection.add(key, this);
                else collection.push(this);
                break;
        }
    },

    moveTo: function(collection, key){
        this._removeWithoutDying();
        switch (collection.getType()){
            case 'value': collection(this); break;
            case 'map' : collection.put(key, this); break;
            case 'set' : collection.add(this); break;
            case 'list' :
                if (key != undefined) collection.add(key, this);
                else collection.push(this);
                break;
        }
    },

    // removes this node from it's parent
    remove: function(){
        if (this.parentCollection){
            var col = this.parentCollection[0];
            var key = this.parentCollection[1];
            if (col.getType() == 'value') col(null);
            else col.remove(key);
        }
    },

    _removeWithoutDying: function(){
        this.removedButNotDead = true;
        this.remove();
    },

    // removes this node from all nodes that has a reference this node
    // if exceptOwnTree: only remove this node from nodes that are not part of this tree
    removeFromReferrers: function(exceptOwnTree){
        var refCount = this.referrers.size();
        var referrer;
        while (referrer = this.referrers.getOne()){
            var col = referrer[0];
            var key = referrer[1];

            // TODO: exceptOwnTree
            if (col.getType() == 'value') col(null);
            else col.remove(key);

            // Prevent endless loops
            if (this.referrers.size() == refCount) throw new Error("Endless loop while trying to remove tree node from its referrers");
            refCount = this.referrers.size();
        }

    },

    forEachMember: function(f, childOnly, nonNullOnly){
        this.collections.forEach(function(name, col){
            if (!childOnly || col.isOwner()){
                if (col.getType() == 'value'){
                    if (!nonNullOnly || col() != null) f(col(), col);
                } else {
                    col.forEach(function(key, val){
                        if (!nonNullOnly || val != null) f(val, col, key);
                    });
                }
            }
        });
    },

    forEachChild: function(f){
        this.forEachMember(f, true, true);
    },

    isChildOf: function(node){
        var parent = this.parent && this.parent();
        if (parent){
            if (parent == node) return true;
            else return parent.isChildOf(node);
        } else return false;
    },

    getPath: function(){
        if (this.parent()){
            var step = {
                node: this.parent(),
                collection: this.parentCollection[0],
                key: this.parentCollection[1]
            };
            return this.parent().getPath().concat(step);
        } else return [];
    },

    getParentCollection: function(){
        return this.parentCollection && this.parentCollection[0];
    },

    isAlive: function(){
        return this.root != null;
    },

    clone: function(){
        return ObservableTreeNode.cloneNodes(this); // only clone this node
    },

    // Tree node handlers:
    // ===================
    onBubble: function(key, handler, priority) { // handlers without priority get default priority 0 and are sorted in insertion order
        sanityCheckHandler(arguments);
        if(!this.handlers.bubble) this.handlers.bubble = new SortedMap();
        this.handlers.bubble.put(key, handler, priority || 0);
    },

    // Add handler is called when this node is added to an owner collection of a parent Node
    onAdd: function(key, handler, priority){
        sanityCheckHandler(arguments);
        if(!this.handlers.add) this.handlers.add = new SortedMap();
        this.handlers.add.put(key, handler, priority || 0);
    },

    onRemove: function(key, handler, priority){
        sanityCheckHandler(arguments);
        if(!this.handlers.remove) this.handlers.remove = new SortedMap();
        this.handlers.remove.put(key, handler, priority || 0);
    },

    // Init handler is called when this node becomes alive, i.e. it has a parent path to a root
    onInit: function(key, handler, priority){
        sanityCheckHandler(arguments);
        if(!this.handlers.init) this.handlers.init = new SortedMap();
        this.handlers.init.put(key, handler, priority || 0);
    },

    onDie: function(key, handler, priority){
        sanityCheckHandler(arguments);
        if(!this.handlers.die) this.handlers.die = new SortedMap();
        this.handlers.die.put(key, handler, priority || 0);
    },

    removeHandlers: function(key){
        for (var type in this.handlers){
            if(this.handlers[type])
                this.handlers[type].remove(key);
        }
    },

    removeAllHandlers: function(col){
        for (var type in this.handlers){
            if(this.handlers[type])
                this.handlers[type].removeAll(col);
        }
    },

    removeHandlersWhere: function(f){
        for (var type in this.handlers){
            if(this.handlers[type])
                this.handlers[type].removeWhere(f);
        }
    },

    // Internal functions
    _addTreeNodeTo: function(parentCollection){
        var self = this;
        var col = parentCollection[0];
        var key = parentCollection[1];
        var parent = col._treeNode;

        if (this.parentCollection){ // If this node already has a parent
            if(this.removedButNotDead) {    //the node was removed using removeWithoutDying and is being added back to the tree
                var oldParent = this.parent();
                var switchingRoot = parent.root !== oldParent.root;
                if (switchingRoot){ //only remove from old root if new root is different
                    if (oldParent.isAlive()) this._removeFromRoot();
                }
                this.parentCollection = parentCollection;
                this.parent(parent);
                if (switchingRoot){ //only add to new root if new root is different than old root
                    if (parent.isAlive()) this._addToRoot(parent.root);
                }
                delete this.removedButNotDead;
            } else {    //not allowed
                throw new Error("Node already has a parent, can't add it to owning collection");
            }

        } else {                    // else assign the new parent
            this.parentCollection = parentCollection;
            this.parent(parent);
            if (parent.isAlive()) this._addToRoot(parent.root);
        }

        if(this.handlers.add)
            this.handlers.add.forEach(function(k, handler){
                handler.call(self, parent, col, key);
            });
    },

    _removeTreeNode: function(){
        var self = this;
        var col = this.parentCollection[0];
        var key = this.parentCollection[1];
        var parent = col._treeNode;

        if (this.removedButNotDead){  // if this node is removed using removeWithoutDying don't change parent
        } else {                        // else entirely remove the node from the tree
            this.parentCollection = null;
            this.parent(null);
            if (this.isAlive()) this._removeFromRoot();
        }
        if(this.handlers.remove)
            this.handlers.remove.forEach(function(k, handler){
                handler.call(self, parent, col, key);
            });
    },

    _addToRoot: function(root){ // TODO: maybe move this function to the root...
        var self = this;
        this.root = root;

        root.handlers.add.forEach(function(k, handler){
            handler.call(root, self);
        });

        if(this.handlers.init)
            this.handlers.init.forEach(function(k, handler){
                handler.call(self);
            });

        this.forEachChild(function(child){  // init children top - down: a child only becomes alive when its parent is already live
            if (child.isObservableTreeNode) child._addToRoot(root);
        });
    },

    _removeFromRoot: function(){
        var self = this;
        this.forEachChild(function(child){  // let children die bottom - up: a parent only dies after all of its children are dead.
            if (child.isObservableTreeNode) child._removeFromRoot();
        });

        this.root.handlers.remove.forEach(function(k, handler){ // TODO: maybe do this after calling die handlers?
            handler.call(self.root, self);
        });

        this.root = null;
        if(this.handlers.die)
            this.handlers.die.forEach(function(k, handler){
                handler.call(self);
            });
    },

    sanityCheck : function() {
        this.forEachMember(function(object, col) {
            if(object && object.isObservableTreeNode && col.isOwner()) {
                object.sanityCheck();
            }
        });
    },
};
extend(ObservableTreeNode, Shadows);

ObservableTreeNode.cloneNodes = function(nodes){
    var cloneMap = new HashMap();

    var cloneNode = function(node){
        var clone = new node.constructor();

        node.collections.forEach(function(name, col){
            if (col.isOwner()){
                var colClone = clone.collections.get(name);
                switch (col.getType()){
                    case 'value':
                        colClone(cloneValue(col()));
                        break;
                    case 'map':
                        col.forEach(function(key, val){  // TODO: fix situation when key is a node reference while map is owner
                            console.assert(!key.isObservableTreeNode, "The use of node reference as key in map not supported by clone");
                            colClone.put(key, cloneValue(val));
                        });
                        break;
                    case 'set':
                        col.forEach(function(val){
                            colClone.add(cloneValue(val));
                        });
                        break;
                    case 'list':
                        col.forEach(function(idx, val){
                            colClone.add(idx, cloneValue(val));
                        });
                        break;
                }
            }
        });

        cloneMap.put(node, clone);
        return clone;
    };

    var cloneValue = function(value){
        if (value == null) return value;
        else if (value.isObservableTreeNode) return cloneNode(value);
        else return cloneObject(value);
    };

    var cloneReferences = function(node, clone){
        node.collections.forEach(function(name, col){
            if (!col.isOwner()){
                var colClone = clone.collections.get(name);
                switch (col.getType()){
                    case 'value':
                        var refClone = cloneMap.get(col());
                        if (refClone) colClone(refClone);
                        break;
                    case 'map':
                        col.forEach(function(key, val){
                            var keyClone = key.isObservableTreeNode ? cloneMap.get(key) : key;
                            var refClone = cloneMap.get(val);
                            if (key != undefined && refClone) colClone.put(keyClone, refClone);
                        });
                        break;
                    case 'set':
                        col.forEach(function(val){
                            var refClone = cloneMap.get(val);
                            if (refClone) colClone.add(refClone);
                        });
                        break;
                    case 'list':
                        col.forEach(function(idx, val){
                            var refClone = cloneMap.get(val);
                            if (refClone) colClone.push(refClone);
                        });
                        break;
                }
            }
        });

        node.forEachChild(function(child){
            if (child && child.isObservableTreeNode) cloneReferences(child, cloneMap.get(child));
        });
    };

    if (nodes.isObservableTreeNode){    // clone only 1 node => returns clone
        var clone = cloneNode(nodes);
        cloneReferences(nodes, clone);
        return clone;
    } else if(nodes instanceof HashSet){    // clone set of nodes => returns map from each node to clone (references between all cloned nodes are cloned as well)
        var result = new HashMap();
        nodes.forEach(function(node){
            result.put(node, cloneNode(node));
        });
        result.forEach(function(node, clone){
            cloneReferences(node, clone);
        });
        return result;
    }
};

function nothing() {
};

ObservableTreeNode.disableAllObservers = function() {
    this.collections.forEach(function(name, col) {
        if(name == "ancestor") return;
        col.removeAllHandlers();
        col.handlers = {};

        if (col.onChange) col.onChange = nothing;
        if (col.onAdd) col.onAdd = nothing;
        if (col.onPut) col.onPut = nothing;
        if (col.onRemove) col.onRemove = nothing;
        if (col.onBubble) col.onBubble = nothing;
        if (col.onSetFail) col.onSetFail = nothing;
    });

    this.shadows.forEach((name, col) => {
        col.removeAllHandlers();
        col.handlers = {};

        if (col.onChange) col.onChange = nothing;
        if (col.onBubble) col.onBubble = nothing;
        if (col.onSetFail) col.onSetFail = nothing;
    });


    this.removeAllHandlers();
    this.handlers = {};
    if (this.onChange) this.onChange = nothing;
    if (this.onAdd) this.onAdd = nothing;
    if (this.onPut) this.onPut = nothing;
    if (this.onRemove) this.onRemove = nothing;
    if (this.onBubble) this.onBubble = nothing;
    if (this.onInit) this.onInit = nothing;
    if (this.onDie) this.onDie = nothing;
};

module.exports = ObservableTreeNode;